/**
 * Settings (super_admin). Campaign-safe master switch with a prominent LIVE / SAFE
 * banner + confirm dialog, tiered-approval threshold, DPDP retention, org/patron copy,
 * and the (off) FCRA switch. Loading / saving / error states throughout.
 */
import { useEffect, useState } from 'react';
import { ShieldAlert, ShieldCheck, Save, Eye, EyeOff } from 'lucide-react';
import { api } from '../lib/apiClient.js';
import { useToast } from '../components/feedback/ToastProvider.jsx';
import { useDialog } from '../components/feedback/DialogProvider.jsx';
import { Card, CardHeader } from '../components/ui/Card.jsx';
import { Field, TextInput } from '../components/ui/Field.jsx';
import styles from './Settings.module.css';

export default function Settings() {
  const toast = useToast();
  const { confirm } = useDialog();
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState('loading');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setStatus('loading');
    try {
      setSettings(await api.get('/settings'));
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  };
  useEffect(() => {
    load();
  }, []);

  const patch = async (body, successMsg) => {
    setSaving(true);
    try {
      const updated = await api.patch('/settings', body);
      setSettings(updated);
      toast.success(successMsg || 'Settings saved.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleCampaignSafe = async () => {
    const turningOn = !settings.campaignSafeMode;
    const ok = await confirm({
      title: turningOn ? 'Enable campaign-safe mode?' : 'Disable campaign-safe mode?',
      message: turningOn
        ? 'Every public surface is anonymised instantly site-wide — names, photos and exact locations are hidden. Enforced server-side.'
        : 'The public wall returns to normal — identifiable info (where consented) becomes visible again.',
      confirmLabel: turningOn ? 'Enable safe mode' : 'Disable safe mode',
      danger: !turningOn,
    });
    if (!ok) return;
    patch(
      { campaignSafeMode: turningOn },
      turningOn ? 'Campaign-safe mode ON — public wall anonymised.' : 'Campaign-safe mode OFF.',
    );
  };

  if (status === 'loading') {
    return <div className={styles.page}><div className={styles.bootCard} /></div>;
  }
  if (status === 'error') {
    return (
      <div className={styles.page}>
        <p className={styles.errorMsg}>Couldn’t load settings. <button onClick={load}>Retry</button></p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <p className="eyebrow">Administration</p>
        <h1 className={styles.h1}>Settings</h1>
      </header>

      {/* Live / Safe master banner */}
      <div className={`${styles.banner} ${settings.campaignSafeMode ? styles.bannerSafe : styles.bannerLive}`}>
        <div className={styles.bannerIcon}>
          {settings.campaignSafeMode ? <ShieldCheck size={26} /> : <Eye size={26} />}
        </div>
        <div className={styles.bannerBody}>
          <strong>{settings.campaignSafeMode ? 'CAMPAIGN-SAFE MODE: ON' : 'PUBLIC WALL: LIVE'}</strong>
          <span>
            {settings.campaignSafeMode
              ? 'All identifiable public info is hidden site-wide, enforced server-side.'
              : 'Names, photos and locations show on the public wall where the beneficiary consented.'}
          </span>
        </div>
        <button
          className={`${styles.switch} ${settings.campaignSafeMode ? styles.switchOn : ''}`}
          onClick={toggleCampaignSafe}
          disabled={saving}
          role="switch"
          aria-checked={settings.campaignSafeMode}
          aria-label="Toggle campaign-safe mode"
        >
          <span className={styles.knob} />
        </button>
      </div>

      <div className={styles.grid}>
        <Card>
          <CardHeader title="Approval & governance" subtitle="Tiered-approval threshold and retention." />
          <ThresholdForm settings={settings} onSave={patch} saving={saving} />
        </Card>

        <Card>
          <CardHeader title="Foreign donations (FCRA)" subtitle="Built as a switch; off until FCRA registration." />
          <div className={styles.fcraRow}>
            <div>
              <strong className={styles.fcraState}>
                {settings.fcraEnabled ? <ShieldAlert size={16} /> : <EyeOff size={16} />}
                {settings.fcraEnabled ? 'Enabled' : 'Disabled'}
              </strong>
              <p className={styles.muted}>Foreign cards stay disabled until this is on.</p>
            </div>
            <button
              className={`${styles.switch} ${settings.fcraEnabled ? styles.switchOn : ''}`}
              onClick={() => patch({ fcraEnabled: !settings.fcraEnabled }, 'FCRA switch updated.')}
              disabled={saving}
              role="switch"
              aria-checked={settings.fcraEnabled}
              aria-label="Toggle FCRA"
            >
              <span className={styles.knob} />
            </button>
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader title="Service area" subtitle="Where the NGO operates. Expansion later is a config change, not a rebuild." />
        <ServiceAreaForm settings={settings} onSave={patch} saving={saving} />
      </Card>

      <Card>
        <CardHeader title="Organisation & patron" subtitle="Shown on the public About section and footer." />
        <OrgForm settings={settings} onSave={patch} saving={saving} />
      </Card>
    </div>
  );
}

function ServiceAreaForm({ settings, onSave, saving }) {
  const area = settings.serviceArea || { constituency: '', blocks: [] };
  const [constituency, setConstituency] = useState(area.constituency || '');
  const [blocks, setBlocks] = useState((area.blocks || []).join(', '));
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const blockList = blocks
          .split(',')
          .map((b) => b.trim())
          .filter(Boolean);
        onSave(
          { serviceArea: { constituency: constituency.trim(), blocks: blockList } },
          'Service area saved.',
        );
      }}
    >
      <Field label="Constituency / region">
        <TextInput value={constituency} onChange={(e) => setConstituency(e.target.value)} placeholder="e.g. Faridkot" />
      </Field>
      <Field label="Blocks / areas" hint="Comma-separated list of the blocks or areas you serve.">
        <TextInput value={blocks} onChange={(e) => setBlocks(e.target.value)} placeholder="Kotkapura, Jaitu, Sadiq" />
      </Field>
      <button className={styles.save} disabled={saving}>
        <Save size={16} /> {saving ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}

function ThresholdForm({ settings, onSave, saving }) {
  const [threshold, setThreshold] = useState(settings.approvalThreshold);
  const [retention, setRetention] = useState(settings.retentionYears);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(
          { approvalThreshold: Number(threshold), retentionYears: Number(retention) },
          'Governance settings saved.',
        );
      }}
    >
      <Field label="Dual-approval threshold (₹)" hint="At or above this amount, two approvers must sign.">
        <TextInput type="number" min="0" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
      </Field>
      <Field label="PII retention (years)" hint="Sensitive PII auto-anonymised this many years after case closure.">
        <TextInput type="number" min="1" max="25" value={retention} onChange={(e) => setRetention(e.target.value)} />
      </Field>
      <button className={styles.save} disabled={saving}>
        <Save size={16} /> {saving ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}

function OrgForm({ settings, onSave, saving }) {
  const [org, setOrg] = useState(settings.org);
  const set = (k) => (e) => setOrg((o) => ({ ...o, [k]: e.target.value }));
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ org }, 'Organisation details saved.');
      }}
    >
      <div className={styles.orgGrid}>
        <Field label="Organisation name">
          <TextInput value={org.name} onChange={set('name')} />
        </Field>
        <Field label="Contact email">
          <TextInput value={org.contactEmail || ''} onChange={set('contactEmail')} type="email" />
        </Field>
      </div>
      <Field label="About">
        <textarea className={styles.textarea} rows={3} value={org.about} onChange={set('about')} />
      </Field>
      <Field label="Patron note" hint="The founder appears once, quietly, as patron.">
        <textarea className={styles.textarea} rows={2} value={org.patron} onChange={set('patron')} />
      </Field>
      <button className={styles.save} disabled={saving}>
        <Save size={16} /> {saving ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}
